import { push } from 'redux-first-history';
import { call, put, all, takeEvery } from 'redux-saga/effects';
import {
  createSystemWebhook,
  deleteSystemWebhook,
  fetchSystemWebhooks,
  updateSystemWebhook,
  // validateSystemWebhook,
} from 'api/SystemWebhooksAPI';
import { getErrorResponse } from 'utils/xhr';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { addToRootSaga } from 'store';

import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { WEBHOOK_ERRORS } from './constants';
// import { WebhookParamProps } from './types';

/**
 * Saga for fetching system webhooks
 */
export function* fetchSystemWebhooksSaga() {
  try {
    const response = yield call(fetchSystemWebhooks);
    yield put(actions.fetchSystemWebhooksResponse(response));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.handleSystemWebhooksError({ errors: [err] }));
    const message = `Error fetching system webhooks. ${err}`;
    yield put(addNegativeToast(message));
  }
}

/**
 * Saga for creating system webhook
 */
export function* createSystemWebhookSaga({ params } /*: WebhookParamProps */) {
  try {
    const data = yield call(createSystemWebhook, params);
    yield put(actions.createSystemWebhookResponse({ payload: data }));
    yield all([
      put(push(`/integrations/webhooks/${data.id}/edit`)),
      put(addPositiveToast('System webhook created.')),
    ]);
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.handleSystemWebhooksError({ errors: [err] }));
    const message = `Error creating system webhook. ${err}`;
    yield put(addNegativeToast(message));
  }
}

/**
 * Saga for deleting system webhook
 */
export function* deleteSystemWebhookSaga({ id }) {
  try {
    yield call(deleteSystemWebhook, id);
    yield all([
      put(actions.fetchSystemWebhooksRequest()),
      put(addPositiveToast('System webhook deleted.')),
    ]);
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.handleSystemWebhooksError({ errors: [err] }));
    const message = `Error deleting system webhook. ${err}`;
    yield put(addNegativeToast(message));
  }
}

/**
 Check the entered data and trigger UI context helpers if invalid
 */
export function* validateSystemWebhookSaga({ params } /* : WebhookParamProps */) {
  try {
    const { authToken, requiresToken, ...paramsWithoutToken } = params;
    const errors = [];

    if (!params.endpoint) {
      errors.push(WEBHOOK_ERRORS.MISSING_URL);
    }

    const requiredTokenMissing = requiresToken && params.authToken?.length === 0;
    if (requiredTokenMissing) {
      errors.push(WEBHOOK_ERRORS.REQUIRES_TOKEN);
    }

    const hasAnEmptyHeader = params.headers?.some((hdr) => hdr.key === '');
    if (hasAnEmptyHeader) {
      errors.push(WEBHOOK_ERRORS.EMPTY_HEADER_FIELD);
    }

    // commented out until the API endpoint exists
    // yield call(validateSystemWebhook, params.endpoint); // if validation throws this will 400 response and hit the catch

    if (errors.length) {
      yield put(actions.handleSystemWebhooksError({ errors }));
      const message = `Webhook has missing or invalid fields.`;
      yield put(addNegativeToast(message));
    } else {
      // the authToken only needs to be included if the endpoint requires it
      yield put(actions.updateSystemWebhookRequest(requiresToken ? params : paramsWithoutToken)); // if everything passes push this through
    }
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.handleSystemWebhooksError({ errors: [err] }));
    const message = `Error updating system webhook.`;
    yield put(addNegativeToast(message));
  }
}

/**
 * Saga for updating system webhook
 */
export function* updateSystemWebhookSaga({ params } /* : WebhookParamProps */) {
  try {
    yield call(updateSystemWebhook, params);
    yield all([
      put(actions.fetchSystemWebhooksRequest()),
      put(addPositiveToast(`Successfully updated webhook.`)),
    ]);
  } catch (error) {
    const err = getErrorResponse(error);
    if (err.includes('is not a valid URL')) {
      yield put(actions.handleSystemWebhooksError({ errors: [WEBHOOK_ERRORS.INVALID_URL] }));
      yield put(addNegativeToast(err));
    } else {
      const message = `Error updating system webhook. ${err}`;
      yield put(addNegativeToast(message));
    }
  }
}

addToRootSaga([
  takeEvery(actionTypes.CREATE_SYSTEM_WEBHOOK_REQUEST, createSystemWebhookSaga),
  takeEvery(actionTypes.DELETE_SYSTEM_WEBHOOK_REQUEST, deleteSystemWebhookSaga),
  takeEvery(actionTypes.FETCH_SYSTEM_WEBHOOKS_REQUEST, fetchSystemWebhooksSaga),
  takeEvery(actionTypes.UPDATE_SYSTEM_WEBHOOK_REQUEST, updateSystemWebhookSaga),
  takeEvery(actionTypes.VALIDATE_SYSTEM_WEBHOOK_REQUEST, validateSystemWebhookSaga),
]);
